import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormFieldConstants } from "utils/constants";
import { FormHandles } from "models/propTypes/genericFormProps";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { RevenueReportingFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik";
import { RevenueReportingCreateFormContainerProps } from "models/propTypes/revenueReportingPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { RevenueReportingCreateForm } from "components/revenueReporting";
import { LoadingWithLogo, useFloatingMessages, useMountedState } from "components/common";
import { useRevenueReportingService } from "serviceHooks";
import RevenueReportingDifferenceFooter from "./revenueReportingDifferenceFooter";
//#endregion
import * as Yup from "yup";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { AccountErrorCodes, MaxAllowedDifference } from "./revenueReportingFormConstants";
import cssExports from "theme/styles/main.module.scss";

const RevenueReportingCreateFormContainer: React.ForwardRefRenderFunction<
  FormHandles,
  RevenueReportingCreateFormContainerProps
> = (props, ref) => {
  const { templates, preselectedTemplateId } = props;
  const showTemplateSelector = templates.length > 1;
  const preselectedTemplate = React.useMemo(
    () => (preselectedTemplateId ? templates.find((template) => template.id === preselectedTemplateId) : undefined),
    [templates, preselectedTemplateId]
  );
  const revenueReportingService = useRevenueReportingService();
  const { getMounted } = useMountedState();
  const { enqueueMessage } = useFloatingMessages();
  const [loading, setLoading] = React.useState(false);

  const [footerDifference, setFooterDifference] = React.useState<{ total: number; balanced: boolean }>({
    total: 0,
    balanced: true,
  });
  const [templateSelected, setTemplateSelected] = React.useState(!!preselectedTemplate);

  const innerRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (innerRef?.current ? innerRef.current : ({} as FormHandles)));

  const selectedTemplateRef = React.useRef<WintSuperkollAPIModel.RevenueReportingTemplate | undefined>(
    templates.length === 1 ? templates[0] : preselectedTemplate
  );

  const { dimensions } = useSelector((store: AppState) => {
    return {
      dimensions: store.common.dimensions.cashReconciliationDimensions ?? [],
    };
  });

  const getTotalAmount = (items: RevenueReportingFormTypes.RevenueReportItem[], isForDifference: boolean) => {
    return (
      items?.reduce(
        (acc, item) =>
          isForDifference
            ? acc +
              (item.accountType === WintSuperkollAPIModel.RevenueReportItemType.Credit
                ? Number(item.amount ?? 0)
                : -1 * Number(item.amount ?? 0))
            : acc + Number(item.amount ?? 0),
        0
      ) ?? 0
    );
  };

  const onItemAmountChange = (
    name: string,
    value: RevenueReportingFormTypes.RevenueReportItem[],
    formikProps: FormikProps<RevenueReportingFormTypes.Create>
  ) => {
    const { values, setFieldValue } = formikProps;

    if (name === FormFieldConstants.RevenueReporting.Create.OtherItems) {
      setFieldValue(FormFieldConstants.RevenueReporting.Create.OthersTotalAmount, getTotalAmount(value, false));
    } else if (name === FormFieldConstants.RevenueReporting.Create.PaymentItems) {
      setFieldValue(FormFieldConstants.RevenueReporting.Create.PaymentsTotalAmount, getTotalAmount(value, false));
    } else {
      setFieldValue(FormFieldConstants.RevenueReporting.Create.SalesTotalAmount, getTotalAmount(value, false));
    }

    const salesItems = name === FormFieldConstants.RevenueReporting.Create.SalesItems ? value : values.salesItems ?? [];
    const paymentItems =
      name === FormFieldConstants.RevenueReporting.Create.PaymentItems ? value : values.paymentItems ?? [];
    const otherItems = name === FormFieldConstants.RevenueReporting.Create.OtherItems ? value : values.otherItems ?? [];

    const difference =
      getTotalAmount(salesItems, true) + getTotalAmount(paymentItems, true) + getTotalAmount(otherItems, true);

    setFieldValue(FormFieldConstants.RevenueReporting.Create.Total, difference);

    const balanced = Math.abs(difference) <= MaxAllowedDifference;
    setFooterDifference({ total: difference, balanced });

    const hasAmounts = getTotalAmountValidity(salesItems, paymentItems, otherItems);
    props.onFormValidationChange && props.onFormValidationChange(hasAmounts && balanced);
  };

  const setTemplateItems = (template: WintSuperkollAPIModel.RevenueReportingTemplate) => {
    const items = template?.items
      ?.slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(
        (item) =>
          ({
            templateItemId: item.id,
            account: item.account,
            accountType: item.accountType,
            description: item.description,
            dimensions: [],
            category: item.category,
            vatAccount: item.vatAccount,
            vatRate: item.vatRate,
          } as RevenueReportingFormTypes.RevenueReportItem)
      );

    innerRef.current?.setValues &&
      innerRef.current.setValues({
        template: template?.name,
        templateId: template?.id,
        salesItems: items?.filter((i) => i.category === WintSuperkollAPIModel.RevenueReportItemCategory.Sales) ?? [],
        paymentItems:
          items?.filter((i) => i.category === WintSuperkollAPIModel.RevenueReportItemCategory.PaymentMethod) ?? [],
        otherItems: items?.filter((i) => i.category === WintSuperkollAPIModel.RevenueReportItemCategory.Other) ?? [],
        salesTotalAmount: 0,
        paymentsTotalAmount: 0,
        othersTotalAmount: 0,
        total: 0,
        dimensions: [],
        hasRowLevelDimensions: false,
      });
  };

  React.useEffect(() => {
    if (selectedTemplateRef.current) {
      setTemplateItems(selectedTemplateRef.current);
    }
    setFooterDifference({ total: 0, balanced: true });
    props.onFormValidationChange && props.onFormValidationChange(false);
  }, []);

  const templateItems = React.useMemo<AutocompleteListItem[]>(
    () =>
      templates
        .filter((template) => template.id)
        .map((template) => ({
          id: template.id ?? "",
          value: template.name ?? "",
        })),
    [templates]
  );

  const onTemplateChange = (item: AutocompleteListItem) => {
    const template = templates.find((t) => t.id === item?.id);
    if (!template) {
      return;
    }

    selectedTemplateRef.current = template;
    setTemplateItems(template);
    setTemplateSelected(true);
    setFooterDifference({ total: 0, balanced: true });
    props.onFormValidationChange && props.onFormValidationChange(false);
  };

  const onTemplateClear = () => {
    selectedTemplateRef.current = undefined;

    innerRef.current?.setValues &&
      innerRef.current.setValues({
        salesItems: [],
        paymentItems: [],
        otherItems: [],
        salesTotalAmount: 0,
        paymentsTotalAmount: 0,
        othersTotalAmount: 0,
        total: 0,
        dimensions: [],
        hasRowLevelDimensions: false,
      });

    setTemplateSelected(false);
    setFooterDifference({ total: 0, balanced: true });
    props.onFormValidationChange && props.onFormValidationChange(false);
  };

  const initialValues = React.useMemo(() => {
    const yesterday = DateHelper.addDays(new Date(), -1);
    const periodLastDay = props.periodLastDay;
    return {
      filesToUpload: [],
      date: periodLastDay && periodLastDay < yesterday ? periodLastDay : yesterday,
      template: selectedTemplateRef.current?.name,
      templateId: selectedTemplateRef.current?.id,
      salesItems: [],
      paymentItems: [],
      otherItems: [],
      dimensions: [],
      hasRowLevelDimensions: false,
      othersTotalAmount: 0,
      salesTotalAmount: 0,
      paymentsTotalAmount: 0,
    };
  }, [props.periodLastDay]);

  const validationSchema = React.useMemo(
    () =>
      Yup.object<RevenueReportingFormTypes.Create>({
        filesToUpload: Yup.array()
          .of(
            Yup.object<UploadedFile>({
              contentType: Yup.string(),
              fileName: Yup.string(),
              fileSize: Yup.number(),
              data: Yup.string(),
            })
          )
          .when(
            FormFieldConstants.RevenueReporting.Create.TemplateId,
            ([templateId]: [string | undefined], schema: ArraySchema<UploadedFile>) =>
              (templates.find((template) => template.id === templateId)?.requiresAttachment &&
                schema
                  .required(TranslationKeys.RevenueReporting.AttachmentRequired)
                  .min(1, TranslationKeys.RevenueReporting.AttachmentRequired)) ||
              schema
          ),
        date: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .required(TranslationKeys.Validations.Required)
          .max(new Date(), TranslationKeys.Validations.InvalidDate),
        template: Yup.string(),
        templateId: Yup.string(),
        dimensions: Yup.array().of(Yup.string()),
        hasRowLevelDimensions: Yup.boolean(),
        salesItems: Yup.array().of(itemSchema()),
        paymentItems: Yup.array().of(itemSchema()),
        otherItems: Yup.array().of(itemSchema()),
        salesTotalAmount: Yup.number(),
        paymentsTotalAmount: Yup.number(),
        othersTotalAmount: Yup.number(),
        total: Yup.number(),
      }),
    [templates]
  );

  function itemSchema() {
    return Yup.object<RevenueReportingFormTypes.RevenueReportItem>({
      templateItemId: Yup.string(),
      account: Yup.number(),
      accountType: Yup.mixed().oneOf(Object.values(WintSuperkollAPIModel.RevenueReportItemType)),
      description: Yup.string(),
      amount: Yup.number(),
      dimensions: Yup.array().of(Yup.string()),
      category: Yup.mixed().oneOf(Object.values(WintSuperkollAPIModel.RevenueReportItemCategory)),
      vatAccount: Yup.number(),
      vatRate: Yup.number(),
    });
  }

  const getTotalAmountValidity = (
    salesItems: RevenueReportingFormTypes.RevenueReportItem[],
    paymentItems: RevenueReportingFormTypes.RevenueReportItem[],
    otherItems: RevenueReportingFormTypes.RevenueReportItem[]
  ) => {
    return salesItems
      .concat(paymentItems)
      .concat(otherItems)
      .some((i) => i.amount);
  };

  const getDimensionShortNames = (selectedDimensions: string[]) => {
    let costCenterShortName;
    let projectShortName;

    const projectId = dimensions.find((d) => d.key.compatibilityType === WintSuperkollAPIModel.DimensionTypes.Project)
      ?.key.id;
    const allDimensions = dimensions.flatMap((d) => d.value);

    selectedDimensions.forEach((dim) => {
      const item = allDimensions.find((a) => a.id === dim);

      item?.typeId && item?.typeId === projectId
        ? (projectShortName = item?.shortName)
        : (costCenterShortName = item?.shortName);
    });

    return { costCenter: costCenterShortName, project: projectShortName };
  };

  const getReportItems = (items: RevenueReportingFormTypes.RevenueReportItem[]) => {
    return items.map((i): WintSuperkollAPIModel.RevenueReportItemDto => {
      const itemDimension = getDimensionShortNames(i.dimensions);
      return {
        templateItemId: i.templateItemId,
        account: i.account,
        amount: Number(i.amount ?? 0),
        accountType: i.accountType,
        category: i.category,
        vatAccount: i.vatAccount,
        vatRate: i.vatRate,
        description: i.description,
        costCenter: itemDimension.costCenter,
        project: itemDimension.project,
      };
    });
  };

  const handleSubmit = (values: RevenueReportingFormTypes.Create) => {
    const reportDate = DateHelper.resetTime(values.date) ?? new Date();
    const { year, month } = DateHelper.getYearMonthByDate(reportDate);

    const report: WintSuperkollAPIModel.RevenueReportDto = {
      templateId: selectedTemplateRef.current?.id,
      date: reportDate,
      periodYear: year,
      periodMonth: month,
      attachments: values.filesToUpload?.map((attachment) => ({
        contentType: attachment.contentType,
        data: attachment.data,
        fileName: attachment.fileName,
      })),
      items: getReportItems(values.salesItems)
        .concat(getReportItems(values.paymentItems))
        .concat(getReportItems(values.otherItems)),
    };

    setLoading(true);
    return revenueReportingService
      .submitReport(report, { hideErrorsForCodes: AccountErrorCodes })
      .catch(onSubmitError)
      .finally(() => {
        if (getMounted()) {
          setLoading(false);
        }
      });
  };

  const onSubmitError = (errors: any) => {
    const isAccountError = errors.codes && AccountErrorCodes.some((code) => errors.codes.includes(code));
    if (getMounted() && errors.message && isAccountError) {
      enqueueMessage(
        `${errors.message} ${LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Errors.PleaseContactSupport
        )}`,
        StyleEnums.DisplayType.Danger,
        true
      );
    }
    throw errors;
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <RevenueReportingCreateForm
        ref={innerRef}
        {...props}
        defaultValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleSubmit}
        templateItems={templateItems}
        showTemplateSelector={showTemplateSelector}
        templateChangeHandler={onTemplateChange}
        templateClearHandler={onTemplateClear}
        itemChangeHandler={onItemAmountChange}
      />
      <RevenueReportingDifferenceFooter
        total={footerDifference.total}
        balanced={footerDifference.balanced}
        show={!showTemplateSelector || templateSelected}
      />
    </>
  );
};

export default React.forwardRef(RevenueReportingCreateFormContainer);
